import { CommonListParams } from "@/types/common";
import request from ".";
import { AddPackBox, AddPackBoxItem, BoxItem, BoxShipStatus, PackBox, PackOrder, PackOrderAdd, PackOrderStatus, PackSourceItem } from "@/types/packing";
import { AddShipment, Logistics, PackAddShip, Shipment } from "@/types/shipment";
import { DictLike } from "@/types/dict";

const URL = '/wms/v2/packing';

export type PackDoType = "void" | "seal-all" | "confirm";
export type BoxDoType = "remove" | "seal" | "unseal";

const APIS = {
  packageTypes: `${URL}/box/package-types`,
  orderList: URL + '/order/list',
  orderFetch: (id: string) => `${URL}/order/${id}`,
  orderSourceItems: (id: string) => `${URL}/order/${id}/sourceItems`,
  packShipments: `${URL}/shipments`,
  orderAdd: URL + '/order/add',
  orderUpdate: (id: string) => `${URL}/order/${id}/update`,
  packOrderOpt: (id: string, type: PackDoType) => `${URL}/order/${id}/${type}`,
  packBoxOpt: (id: string, type: BoxDoType) => `${URL}/box/${id}/${type}`,
  boxAdd: (id: string) => `${URL}/order/${id}/box`,
  boxEdit: (id: string) => `${URL}/box/${id}/update`,
  boxAddItem: (id: string) => `${URL}/box/${id}/item/add`,
  boxShipStatus: (id: string) => `/wms/v2/shipment/${id}/boxes`,
  boxEditItem: (id: string) => `${URL}/item/${id}/update`,
  boxDelItem: (id: string) => `${URL}/item/${id}/remove`,
  packLogistcs: (id: string) => `${URL}/order/${id}/logistics`,
  packAddShip: `/wms/v2/shipment/order/fromBoxes`,
  addOutInv: (id: string) => `${URL}/order/${id}/source/add`,
};

export const packageTypes = () => {
  return new Promise<Array<DictLike>>((resolve, reject) => {
    request<Array<DictLike>, undefined>(APIS.packageTypes)
      .then(res => resolve(res.body))
      .catch(err => reject(err))
  })
};

export interface PackListParams {
  status?: PackOrderStatus;
  number?: string;
  sourceOrderId?: string;
  sourceOrderNumber?: string;
  orgId?: string;
  startTime?: number;
  endTime?: number;
};

export const orderList = (params: CommonListParams<PackListParams>) => {
  return new Promise<CommonListParams<Array<PackOrder>>>((resolve, reject) => {
    request<Array<PackOrder>, PackListParams>(APIS.orderList, { params })
      .then((res) => resolve(res))
      .catch((err) => reject(err));
  })
};

export const orderFetch = (id: string) => {
  return new Promise<PackOrder>((resolve, reject) => {
    request<PackOrder, string>(APIS.orderFetch(id))
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const orderSourceItems = (id: string) => {
  return new Promise<PackSourceItem[]>((resolve, reject) => {
    request<PackSourceItem[], string>(APIS.orderSourceItems(id))
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

// G03.D06.M37.I25：按装箱单 ID 集合经 PackingBoxShipment 反查关联发货单；空入参返回空列表。
export const packShipments = (body: string[], controller?: AbortController) => {
  return new Promise<Shipment[]>((resolve, reject) => {
    request<Shipment[], string[]>(APIS.packShipments, { params: { body }, controller })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const orderAdd = (body: PackOrderAdd) => {
  return new Promise<PackOrder>((resolve, reject) => {
    request<PackOrder, PackOrderAdd>(APIS.orderAdd, { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const orderUpdate = (id: string, body: PackOrderAdd) => {
  return new Promise<PackOrder>((resolve, reject) => {
    request<PackOrder, PackOrderAdd>(APIS.orderUpdate(id), { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const packOrderOpt = (id: string, type: PackDoType) => {
  return new Promise<void>((resolve, reject) => {
    request<void, undefined>(APIS.packOrderOpt(id, type))
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export const packBoxOpt = (id: string, type: BoxDoType) => {
  return new Promise<void>((resolve, reject) => {
    request<void, undefined>(APIS.packBoxOpt(id, type))
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};

export const boxAdd = (id: string, body: AddPackBox) => {
  return new Promise<PackBox>((resolve, reject) => {
    request<PackBox, AddPackBox>(APIS.boxAdd(id), { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const boxEdit = (id: string, body: Partial<AddPackBox>) => {
  return new Promise<PackBox>((resolve, reject) => {
    request<PackBox, Partial<AddPackBox>>(APIS.boxEdit(id), { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const boxAddItem = (id: string, body: AddPackBoxItem) => {
  return new Promise<BoxItem>((resolve, reject) => {
    request<BoxItem, AddPackBoxItem>(APIS.boxAddItem(id), { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const boxShipStatus = (id: string) => {
  return new Promise<BoxShipStatus[]>((resolve, reject) => {
    request<BoxShipStatus[], undefined>(APIS.boxShipStatus(id))
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const boxEditItem = (id: string, body: AddPackBoxItem) => {
  return new Promise<BoxItem>((resolve, reject) => {
    request<BoxItem, AddPackBoxItem>(APIS.boxEditItem(id), { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const boxDelItem = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    request<void, AddPackBoxItem>(APIS.boxDelItem(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};


export const packLogistcs = (id: string) => {
  return new Promise<Logistics[] | null>((resolve, reject) => {
    request<Logistics[] | null, undefined>(APIS.packLogistcs(id))
      .then(({ body }) => resolve(body))
      .catch(err => reject(err))
  })
};

export const packAddShip = (body: PackAddShip) => {
  return new Promise<Shipment>((resolve, reject) => {
    request<Shipment, PackAddShip>(APIS.packAddShip, { params: { body } })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
};

export const addOutInv = (id: string, body: string[]) => {
  return new Promise<void>((resolve, reject) => {
    request<void, string[]>(APIS.addOutInv(id), { params: { body } })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
};
